/**
 * A turn a signal started stays out of the thread until the agent decides to
 * speak (RFC 0301 §3f).
 *
 * A comment on a paired document, or a mention, reaches the session as an event
 * and queues a turn. The event itself is for the agent. Drawn as a card it put a
 * row in the thread before anything had been decided, and a turn that passed
 * still left its tool calls behind. So the event never draws, and the turn after
 * it draws only if it said something or did something a card shows: a document
 * written, a comment answered, a session started.
 *
 * PURE, over the projection, so the rule is testable without a repo and the
 * reading status reads the same segments the thread hides.
 */

import type { TranscriptMessage } from '@mod/core/model'
import { bareToolName, cardForCall } from './threadCards'
import { argString } from './toolArgs'

/** Events that start a turn the agent may pass on. */
const QUIET_KINDS: ReadonlySet<string> = new Set([
  'comment_claimed',
  'mentioned',
  'edited',
])

/**
 * Events for the agent alone (RFC 0303 3e): the person pressed Take over or
 * Hand back on a web pane, and saw themselves do it. They never draw, and
 * unlike a quiet event they hide nothing after them: the turn a hand back
 * starts is the agent carrying on, which draws as any turn does.
 */
const UNDRAWN_KINDS: ReadonlySet<string> = new Set([
  'page_taken_over',
  'page_handed_back',
])

const isUndrawn = (m: TranscriptMessage): boolean =>
  m.role === 'system' && !!m.kind && UNDRAWN_KINDS.has(m.kind)

const isQuietEvent = (m: TranscriptMessage): boolean =>
  m.role === 'system' && !!m.kind && QUIET_KINDS.has(m.kind)

/**
 * Events that are a comment somebody wrote (RFC 0324 3d). Unlike an edit, the
 * comment itself draws, as a card: it is what the agent answers, and a
 * transcript without it has replies to nothing.
 */
export const COMMENT_EVENT_KINDS: ReadonlySet<string> = new Set([
  'comment_claimed',
  'mentioned',
  'comment_reply',
])

export const isCommentEvent = (m: TranscriptMessage): boolean =>
  m.role === 'system' && !!m.kind && COMMENT_EVENT_KINDS.has(m.kind)

/** Anything that starts the next turn ends this one's segment. */
const startsTurn = (m: TranscriptMessage): boolean =>
  m.role === 'user' || (m.role === 'system' && !!m.kind)

/** Did the agent decide something a person should see? */
function spoke(segment: TranscriptMessage[]): boolean {
  return segment.some(
    (m) =>
      m.role === 'assistant' &&
      m.content.some((b) =>
        b.type === 'text'
          ? b.text.trim().length > 0
          : b.type === 'tool_use' &&
            cardForCall({ id: b.id, name: b.name, arguments: b.arguments }) !==
              null,
      ),
  )
}

interface Segment {
  /** Index of the quiet event. */
  at: number
  /** The messages after it that belong to its turn. */
  messages: TranscriptMessage[]
  spoke: boolean
}

function segments(transcript: TranscriptMessage[]): Segment[] {
  const out: Segment[] = []
  for (let i = 0; i < transcript.length; i++) {
    if (!isQuietEvent(transcript[i] as TranscriptMessage)) continue
    const messages: TranscriptMessage[] = []
    let j = i + 1
    while (
      j < transcript.length &&
      !startsTurn(transcript[j] as TranscriptMessage)
    ) {
      messages.push(transcript[j] as TranscriptMessage)
      j++
    }
    out.push({ at: i, messages, spoke: spoke(messages) })
  }
  return out
}

/** The transcript as the thread draws it: quiet events removed, except a
 *  comment, which stays as its card, and the turns after them removed unless
 *  the agent spoke. */
export function hideQuietTurns(
  transcript: TranscriptMessage[],
): TranscriptMessage[] {
  const hidden = new Set<TranscriptMessage>()
  for (const s of segments(transcript)) {
    const event = transcript[s.at] as TranscriptMessage
    if (!isCommentEvent(event)) hidden.add(event)
    if (!s.spoke) for (const m of s.messages) hidden.add(m)
  }
  for (const m of transcript) if (isUndrawn(m)) hidden.add(m)
  return hidden.size === 0
    ? transcript
    : transcript.filter((m) => !hidden.has(m))
}

/**
 * The comments the agent read and chose not to answer, with the note it gave.
 *
 * A turn that passed is hidden, but the comment that started it still draws,
 * and "read, no reply" is the one thing the person needs to know about it
 * (RFC 0324 3d). Keyed by the event message; the note is '' when it gave none.
 */
export function passedComments(
  transcript: TranscriptMessage[],
): Map<TranscriptMessage, string> {
  const out = new Map<TranscriptMessage, string>()
  for (const s of segments(transcript)) {
    const event = transcript[s.at] as TranscriptMessage
    if (s.spoke || !isCommentEvent(event)) continue
    for (const m of s.messages) {
      if (m.role !== 'assistant') continue
      for (const b of m.content) {
        if (b.type !== 'tool_use' || bareToolName(b.name) !== 'pass') continue
        const args = (b.arguments ?? {}) as Record<string, unknown>
        out.set(event, (argString(args.note) ?? '').trim())
      }
    }
  }
  return out
}

/**
 * The session is reading: a worker has claimed a turn a quiet event started,
 * and the agent has not yet spoken. `claimed` comes from the run, because the
 * transcript cannot tell a queued turn from a claimed one.
 */
export function isReading(
  transcript: TranscriptMessage[],
  claimed: boolean,
): boolean {
  if (!claimed) return false
  const last = segments(transcript).at(-1)
  if (!last || last.spoke) return false
  // Only while that segment is the end of the transcript: a later message
  // means the claimed turn is about something else.
  return last.at + last.messages.length === transcript.length - 1
}
